import React, { useState, useEffect } from 'react';
import {
  Box,
  Text,
  Flex,
  Icon,
  useColorModeValue,
  Button,
  Input,
  Select,
  FormControl,
  InputGroup,
  InputLeftAddon,
  FormErrorMessage,
  Spinner,
} from "@chakra-ui/react";
import { FiArrowLeft, FiEdit } from "react-icons/fi";
import Card from 'components/card/Card';
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from 'react-redux';
import { fetchTermDetails, updateTerm } from 'features/terms/actions';
import { useToastNotification } from 'utils/toast-utils';

const termSchema = Yup.object().shape({
  title: Yup.string().required("Title is required"),
  escrowReserveRate: Yup.number()
    .min(0, "Must be greater than or equal to 0")
    .max(100, "Must be less than or equal to 100")
    .required("Escrow reserve rate is required"),
  advanceRate: Yup.number()
    .min(0, "Must be greater than or equal to 0")
    .max(100, "Must be less than or equal to 100")
    .required("Advance rate is required")
    .test('advance-rate-validation', 'Advance rate must equal 100% - purchase rate - escrow rate', 
      function(value) {
        const { purchaseRate, escrowReserveRate } = this.parent;
        return value === 100 - Number(purchaseRate) - Number(escrowReserveRate);
      }),
  minimumPurchaseFee: Yup.number()
    .min(0, "Must be greater than or equal to 0")
    .required("Minimum purchase fee is required"),
  purchaseRate: Yup.number()
    .min(0, "Must be greater than or equal to 0")
    .max(100, "Must be less than or equal to 100")
    .required("Purchase rate is required"),
  feePerInvoice: Yup.number()
    .min(0, "Must be greater than or equal to 0")
    .required("Fee per invoice is required"),
  status: Yup.string().required("Status is required"),
});

const TermsDetails = ({ termsData, onBack, isEditMode }) => {
  const dispatch = useDispatch();
  const { selectedTerm, loading, error } = useSelector((state) => state.terms);
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
  const [isEditingInfo, setIsEditingInfo] = useState(isEditMode);
  const [isEditingFees, setIsEditingFees] = useState(isEditMode);
  const { showToast } = useToastNotification();
  const [isSavingInfo, setIsSavingInfo] = useState(false);
  const [isSavingFees, setIsSavingFees] = useState(false);

  useEffect(() => {
    setIsEditingInfo(isEditMode);
    setIsEditingFees(isEditMode);
    if (termsData?._id) {
      dispatch(fetchTermDetails(termsData._id));
    }
  }, [isEditMode, dispatch, termsData?._id]);

  const calculateAdvanceRate = (purchaseRate, escrowRate) => {
    return 100 - Number(purchaseRate) - Number(escrowRate);
  };

  const formik = useFormik({
    initialValues: {
      title: selectedTerm?.term?.title || termsData?.title || '',
      escrowReserveRate: selectedTerm?.term?.escrowReserveRate || termsData?.escrowReserveRate || 0,
      advanceRate: selectedTerm?.term?.advanceRate || termsData?.advanceRate || 0,
      minimumPurchaseFee: selectedTerm?.term?.minimumPurchaseFee || termsData?.minimumPurchaseFee || 0,
      purchaseRate: selectedTerm?.term?.purchaseRate || termsData?.purchaseRate || 0,
      feePerInvoice: selectedTerm?.term?.feePerInvoice || termsData?.feePerInvoice || 0,
      status: selectedTerm?.term?.status || termsData?.status || 'ACTIVE'
    },
    validationSchema: termSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setIsSavingInfo(true);
      setIsSavingFees(true);
      try {
        const updatedValues = {
          ...values,
          advanceRate: calculateAdvanceRate(values.purchaseRate, values.escrowReserveRate)
        };
        await dispatch(updateTerm({
          termId: termsData._id,
          termData: {
            title: updatedValues.title,
            escrowReserveRate: Number(updatedValues.escrowReserveRate),
            advanceRate: Number(updatedValues.advanceRate),
            minimumPurchaseFee: Number(updatedValues.minimumPurchaseFee),
            purchaseRate: Number(updatedValues.purchaseRate),
            feePerInvoice: Number(updatedValues.feePerInvoice),
            status: updatedValues.status
          }
        })).unwrap();
        showToast('Success', 'Term updated successfully', 'success');
        setIsEditingInfo(false);
        setIsEditingFees(false);
      } catch (error) {
        showToast('Error', error.message || 'Failed to update term', 'error');
        console.error("Error updating terms:", error);
      } finally {
        setIsSavingInfo(false);
        setIsSavingFees(false);
      }
    },
  });

  useEffect(() => {
    if (isEditingFees) {
      const newAdvanceRate = calculateAdvanceRate(
        formik.values.purchaseRate,
        formik.values.escrowReserveRate
      );
      formik.setFieldValue('advanceRate', newAdvanceRate);
    }
  }, [formik.values.purchaseRate, formik.values.escrowReserveRate, isEditingFees]);

  const handleInfoEdit = () => {
    setIsEditingInfo(true);
  };

  const handleFeesEdit = () => {
    setIsEditingFees(true);
  };

  const handleInfoSave = async () => {
    setIsSavingInfo(true);
    try {
      await dispatch(updateTerm({
        termId: termsData._id,
        termData: {
          title: formik.values.title,
          status: formik.values.status
        }
      })).unwrap();
      showToast('Success', 'Information updated successfully', 'success');
      setIsEditingInfo(false);
    } catch (error) {
      showToast('Error', error.message || 'Failed to update information', 'error');
      console.error("Error updating information:", error);
    } finally {
      setIsSavingInfo(false);
    }
  };

  const handleFeesSave = async () => {
    setIsSavingFees(true);
    try {
      await dispatch(updateTerm({
        termId: termsData._id,
        termData: {
          escrowReserveRate: Number(formik.values.escrowReserveRate),
          advanceRate: Number(formik.values.advanceRate),
          minimumPurchaseFee: Number(formik.values.minimumPurchaseFee),
          purchaseRate: Number(formik.values.purchaseRate),
          feePerInvoice: Number(formik.values.feePerInvoice)
        }
      })).unwrap();
      showToast('Success', 'Purchase fees updated successfully', 'success');
      setIsEditingFees(false);
    } catch (error) {
      showToast('Error', error.message || 'Failed to update purchase fees', 'error');
      console.error("Error updating purchase fees:", error);
    } finally {
      setIsSavingFees(false);
    }
  };

  const handleInfoCancel = () => {
    formik.setFieldValue('title', selectedTerm?.term?.title || termsData?.title || '');
    formik.setFieldValue('status', selectedTerm?.term?.status || termsData?.status || 'ACTIVE');
    setIsEditingInfo(false);
  };

  const handleFeesCancel = () => {
    formik.setFieldValue('escrowReserveRate', selectedTerm?.term?.escrowReserveRate || termsData?.escrowReserveRate || 0);
    formik.setFieldValue('advanceRate', selectedTerm?.term?.advanceRate || termsData?.advanceRate || 0);
    formik.setFieldValue('minimumPurchaseFee', selectedTerm?.term?.minimumPurchaseFee || termsData?.minimumPurchaseFee || 0);
    formik.setFieldValue('purchaseRate', selectedTerm?.term?.purchaseRate || termsData?.purchaseRate || 0);
    formik.setFieldValue('feePerInvoice', selectedTerm?.term?.feePerInvoice || termsData?.feePerInvoice || 0);
    setIsEditingFees(false);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatPercentage = (value) => {
    return `${parseFloat(value).toFixed(2)}%`;
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" minH="200px">
        <Spinner size="xl" color="purple.500" />
      </Flex>
    );
  }

  return (
    <Box p="0px">
      <Card p="0px">
        <Flex direction="column" gap={4}>
          {/* Header */}
          <Flex align="center" mb={4}>
            <Icon
              as={FiArrowLeft}
              w={6}
              h={6}
              cursor="pointer"
              onClick={onBack}
              mr={4}
            />
            <Box flex="1">
              <Text fontSize="2xl" fontWeight="bold">Terms Details</Text>
            </Box>
          </Flex>

          {/* Information Section */}
          <Card p="24px" borderRadius="8px" boxShadow="0px 0px 12px 0px #0000001A">
            <Flex justify="space-between" align="center" mb={6}>
              <Text fontSize="1rem" fontWeight="600" color="#42505C">
                Information
              </Text>
              {isEditingInfo ? (
                <Flex gap={2}>
                  <Button 
                    size="sm" 
                    colorScheme="purple" 
                    onClick={handleInfoSave}
                    isLoading={isSavingInfo}
                    loadingText="Saving..."
                    isDisabled={isSavingInfo || isSavingFees || !formik.isValid}
                  >
                    Save
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={handleInfoCancel}
                    isDisabled={isSavingInfo || isSavingFees}
                  >
                    Cancel
                  </Button>
                </Flex>
              ) : (
                <Icon
                  as={FiEdit}
                  w={5}
                  h={5}
                  cursor="pointer"
                  color="purple.500"
                  onClick={handleInfoEdit}
                />
              )}
            </Flex>
            <Flex direction="column" gap={6}>
              <Flex gap={4}>
                <Box flex={1}>
                  <FormControl
                    isRequired
                    isInvalid={formik.touched.title && formik.errors.title}
                  >
                    <Text color="black.700" mb={2} fontWeight="600">Term Title</Text>
                    {isEditingInfo ? (
                      <Input
                        value={formik.values.title}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        name="title"
                      />
                    ) : (
                      <Text color={textColor} fontWeight="500">
                        {formik.values.title}
                      </Text>
                    )}
                    <FormErrorMessage>{formik.errors.title}</FormErrorMessage>
                  </FormControl>
                </Box>
                <Box flex={1}>
                  <Text color="black.700" mb={2} fontWeight="600">Status</Text>
                  {isEditingInfo ? (
                    <Select
                      value={formik.values.status}
                      onChange={(e) => formik.setFieldValue('status', e.target.value)}
                    >
                      <option value="ACTIVE">Active</option>
                      <option value="INACTIVE">Inactive</option>
                    </Select>
                  ) : (
                    <Text color={formik.values.status === "ACTIVE" ? "green.500" : "red.500"} fontWeight="500">
                      {formik.values.status === "ACTIVE" ? "Active" : "Inactive"}
                    </Text>
                  )}
                </Box>
              </Flex>
            </Flex>
          </Card>

          {/* Purchase Fee Section */}
          <Card p="24px" borderRadius="8px" boxShadow="0px 0px 12px 0px #0000001A">
            <Flex justify="space-between" align="center" mb={6}>
              <Text fontSize="1rem" fontWeight="600" color="#42505C">
                Purchase Fee
              </Text>
              {isEditingFees ? (
                <Flex gap={2}>
                  <Button 
                    size="sm" 
                    colorScheme="purple" 
                    onClick={handleFeesSave}
                    isLoading={isSavingFees}
                    loadingText="Saving..."
                    isDisabled={isSavingInfo || isSavingFees || !formik.isValid}
                  >
                    Save
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={handleFeesCancel}
                    isDisabled={isSavingInfo || isSavingFees}
                  >
                    Cancel
                  </Button>
                </Flex>
              ) : (
                <Icon
                  as={FiEdit}
                  w={5}
                  h={5}
                  cursor="pointer"
                  color="purple.500"
                  onClick={handleFeesEdit}
                />
              )}
            </Flex>
            <Flex direction="column" gap={6}>
              <Flex gap={4}>
                <Box flex={1}>
                  <FormControl
                    isRequired
                    isInvalid={formik.touched.escrowReserveRate && formik.errors.escrowReserveRate}
                  >
                    <Text color="black.700" mb={2} fontWeight="600">Escrow Reserve Rate</Text>
                    {isEditingFees ? (
                      <InputGroup>
                        <InputLeftAddon children="%" />
                        <Input
                          type="number"
                          name="escrowReserveRate"
                          value={formik.values.escrowReserveRate}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                      </InputGroup>
                    ) : (
                      <Text color={textColor} fontWeight="500">
                        {formatPercentage(formik.values.escrowReserveRate)}
                      </Text>
                    )}
                    <FormErrorMessage>{formik.errors.escrowReserveRate}</FormErrorMessage>
                  </FormControl>
                </Box>
                <Box flex={1}>
                  <Text color="black.700" mb={2} fontWeight="600">Purchase Rate</Text>
                  {isEditingFees ? (
                    <Input
                      type="number"
                      value={formik.values.purchaseRate}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      name="purchaseRate"
                    />
                  ) : (
                    <Text color={textColor} fontWeight="500">
                      {formatPercentage(formik.values.purchaseRate)}
                    </Text>
                  )}
                </Box>
              </Flex>
              <Flex gap={4}>
                <Box flex={1}>
                  <Text color="black.700" mb={2} fontWeight="600">Advance Rate</Text>
                  {isEditingFees ? (
                    <Input
                      type="number"
                      value={formik.values.advanceRate}
                      isReadOnly
                      bg="gray.100"
                      _hover={{ bg: "gray.100" }}
                      _focus={{ bg: "gray.100" }}
                    />
                  ) : (
                    <Text color={textColor} fontWeight="500">
                      {formatPercentage(formik.values.advanceRate)}
                    </Text>
                  )}
                </Box>
                <Box flex={1}>
                  <Text color="black.700" mb={2} fontWeight="600">Fee Per Invoice</Text>
                  {isEditingFees ? (
                    <Input
                      type="number"
                      value={formik.values.feePerInvoice}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      name="feePerInvoice"
                    />
                  ) : (
                    <Text color={textColor} fontWeight="500">
                      {formatCurrency(formik.values.feePerInvoice)}
                    </Text>
                  )}
                </Box>
              </Flex>
              <Flex gap={4}>
                <Box flex={1}>
                  <Text color="black.700" mb={2} fontWeight="600">Minimum Purchase Fee</Text>
                  {isEditingFees ? (
                    <Input
                      type="number"
                      value={formik.values.minimumPurchaseFee}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      name="minimumPurchaseFee"
                    />
                  ) : (
                    <Text color={textColor} fontWeight="500">
                      {formatCurrency(formik.values.minimumPurchaseFee)}
                    </Text>
                  )}
                </Box>
                <Box flex={1} />
              </Flex>
            </Flex>
          </Card>
        </Flex>
      </Card>
    </Box>
  );
};

export default TermsDetails;
